import * as DialogPrimitive from "@radix-ui/react-dialog";
import { atom, useAtom, useSetAtom } from "jotai";
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, ExpandIcon, XIcon } from "lucide-react";
import { type ReactNode, useCallback } from "react";
import { ArtifactImage } from "@/components/artifacts/artifact-image";
import { ArtifactVideo } from "@/components/artifacts/artifact-video";
import { downloadArtifactFile } from "@/lib/artifact-cache";
import { cn } from "@/lib/utils";
import { useRecord } from "@/sync/hooks";

type LightboxState = { ids: string[]; index: number };

const lightboxAtom = atom<LightboxState | null>(null);

/** Opens the app-wide media viewer on a set of artifacts (images and videos). */
export function useLightbox() {
	const set = useSetAtom(lightboxAtom);
	return useCallback(
		(ids: string[], index = 0) => {
			if (ids.length) set({ ids, index: Math.max(0, Math.min(index, ids.length - 1)) });
		},
		[set],
	);
}

/** Click-to-enlarge wrapper around a media thumbnail. Not for use inside links or buttons. */
export function Zoomable({
	ids,
	index = 0,
	className,
	children,
}: {
	ids: string[];
	index?: number;
	className?: string;
	children: ReactNode;
}) {
	const open = useLightbox();
	return (
		<button
			type="button"
			aria-label="View full size"
			onClick={(event) => {
				event.stopPropagation();
				open(ids, index);
			}}
			className={cn(
				"nodrag block cursor-zoom-in outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
				className,
			)}
		>
			{children}
		</button>
	);
}

/** Small corner button for media whose own click does something else. */
export function ExpandButton({
	ids,
	index = 0,
	className,
}: {
	ids: string[];
	index?: number;
	className?: string;
}) {
	const open = useLightbox();
	return (
		<button
			type="button"
			aria-label="View full size"
			title="View full size"
			onClick={(event) => {
				event.preventDefault();
				event.stopPropagation();
				open(ids, index);
			}}
			className={cn(
				"nodrag absolute top-1 right-1 flex size-6 items-center justify-center rounded-md bg-black/55 text-white opacity-0 backdrop-blur transition-opacity duration-100 group-hover:opacity-100 focus-visible:opacity-100 [&_svg]:size-3.5",
				className,
			)}
		>
			<ExpandIcon />
		</button>
	);
}

function Slide({ artifactId }: { artifactId: string }) {
	const artifact = useRecord("artifact", artifactId);
	if (artifact?.kind === "video") {
		return (
			<ArtifactVideo
				key={artifactId}
				artifactId={artifactId}
				autoLoad
				className="max-h-full max-w-full rounded-md"
			/>
		);
	}
	return (
		<ArtifactImage
			key={artifactId}
			artifactId={artifactId}
			variant="original"
			fit="contain"
			className="max-h-full max-w-full rounded-md"
		/>
	);
}

/** Mounted once in the app shell. ← → step, Esc closes. */
export function LightboxHost() {
	const [state, setState] = useAtom(lightboxAtom);
	const ids = state?.ids ?? [];
	const index = state?.index ?? 0;
	const current = ids[index];
	const artifact = useRecord("artifact", current);
	const step = (dir: 1 | -1) =>
		setState((prev) =>
			prev ? { ...prev, index: (prev.index + dir + prev.ids.length) % prev.ids.length } : prev,
		);
	return (
		<DialogPrimitive.Root open={!!state} onOpenChange={(open) => !open && setState(null)}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/90 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
				<DialogPrimitive.Content
					className="fixed inset-0 z-[60] flex flex-col outline-none"
					onKeyDown={(event) => {
						if (ids.length < 2) return;
						if (event.key === "ArrowLeft") step(-1);
						if (event.key === "ArrowRight") step(1);
					}}
				>
					<DialogPrimitive.Title className="sr-only">
						{artifact?.title || "Media viewer"}
					</DialogPrimitive.Title>
					<DialogPrimitive.Description className="sr-only">
						Full-size media. Use the arrow keys to step through.
					</DialogPrimitive.Description>
					<div className="flex h-12 shrink-0 items-center gap-3 px-4 text-[13px] text-white/80">
						<span className="min-w-0 flex-1 truncate">{artifact?.title}</span>
						{ids.length > 1 && (
							<span className="font-mono text-[12px] tabular-nums">
								{index + 1}/{ids.length}
							</span>
						)}
						{artifact && (
							<button
								type="button"
								aria-label="Download"
								title="Download"
								onClick={() => void downloadArtifactFile(artifact)}
								className="flex size-8 items-center justify-center rounded-md hover:bg-white/10 [&_svg]:size-4"
							>
								<DownloadIcon />
							</button>
						)}
						<DialogPrimitive.Close
							aria-label="Close"
							className="flex size-8 items-center justify-center rounded-md hover:bg-white/10 [&_svg]:size-4"
						>
							<XIcon />
						</DialogPrimitive.Close>
					</div>
					<div className="relative flex min-h-0 flex-1 items-center justify-center px-14 pb-4">
						{current && <Slide artifactId={current} />}
						{ids.length > 1 && (
							<>
								<button
									type="button"
									aria-label="Previous"
									onClick={() => step(-1)}
									className="absolute top-1/2 left-3 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 [&_svg]:size-5"
								>
									<ChevronLeftIcon />
								</button>
								<button
									type="button"
									aria-label="Next"
									onClick={() => step(1)}
									className="absolute top-1/2 right-3 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 [&_svg]:size-5"
								>
									<ChevronRightIcon />
								</button>
							</>
						)}
					</div>
					{ids.length > 1 && (
						<div className="flex shrink-0 justify-center gap-1.5 overflow-x-auto px-4 pb-4">
							{ids.map((id, i) => (
								<button
									key={id}
									type="button"
									aria-label={`Show item ${i + 1}`}
									onClick={() => setState({ ids, index: i })}
									className={cn(
										"size-12 shrink-0 overflow-hidden rounded-[4px] border border-white/20 transition-opacity duration-100",
										i === index ? "border-white opacity-100" : "opacity-50 hover:opacity-100",
									)}
								>
									<ArtifactImage artifactId={id} className="size-full" />
								</button>
							))}
						</div>
					)}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
